import { formatDateTime } from "@/utils/dateTime";
import { useState } from "react";
import type { Key, ReactElement } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import {
  approveMerchantWithdrawals,
  exportMerchantWithdrawals,
  listMerchantWithdrawals,
  rejectMerchantWithdrawals,
} from "@/api/shopMerchant";
import type {
  MerchantId,
  MerchantWithdrawal,
  MerchantWithdrawQuery,
} from "@/types/shopMerchant";
import { useShopList } from "../useShopList";

interface SearchValues {
  merchantId?: number;
  merchantName?: string;
  merchantAccount?: string;
  status?: number;
  range?: [Dayjs, Dayjs];
}
const initialQuery: MerchantWithdrawQuery = { ypageNum: 1, ypageSize: 10 };
const statuses = ["待审核", "审核通过", "已驳回", "已到账"];
const statusColors = ["orange", "green", "red", "blue"];

function MerchantWithdrawalReview(): ReactElement {
  const [form] = Form.useForm<SearchValues>();
  const [rejectForm] = Form.useForm<{ auditRemark: string }>();
  const [query, setQuery] = useState(initialQuery);
  const { rows, total, loading, refresh } = useShopList(
    listMerchantWithdrawals,
    query
  );
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [rejectIds, setRejectIds] = useState<MerchantId[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const selectedIds = rows
    .filter((row) => row.status === 0 && selectedKeys.includes(row.id))
    .map((row) => row.id);

  const handleApprove = (ids: MerchantId[]): void => {
    if (!ids.length || submitting) return;
    Modal.confirm({
      title: "通过提现审核",
      content: `确认通过这 ${ids.length} 笔提现？`,
      onOk: async (): Promise<void> => {
        setSubmitting(true);
        try {
          await approveMerchantWithdrawals(ids);
          message.success("提现审核已通过");
          setSelectedKeys([]);
          refresh();
        } finally {
          setSubmitting(false);
        }
      },
    });
  };
  const handleOpenReject = (ids: MerchantId[]): void => {
    if (!ids.length || submitting) return;
    rejectForm.resetFields();
    setRejectIds(ids);
  };
  const handleReject = async (values: {
    auditRemark: string;
  }): Promise<void> => {
    if (!rejectIds.length) return;
    setSubmitting(true);
    try {
      await rejectMerchantWithdrawals(rejectIds, values.auditRemark.trim());
      message.success("已驳回，金额已退回商户可提现余额");
      setRejectIds([]);
      setSelectedKeys([]);
      refresh();
    } catch {
      /* 请求层展示后端错误。 */
    } finally {
      setSubmitting(false);
    }
  };
  const handleExport = async (): Promise<void> => {
    setExporting(true);
    try {
      const { ypageNum, ypageSize, ...filters } = query;
      const blob = await exportMerchantWithdrawals(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "商家提现管理.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      /* 请求层展示后端错误。 */
    } finally {
      setExporting(false);
    }
  };
  const handleSearch = (values: SearchValues): void => {
    setSelectedKeys([]);
    setQuery({
      ...initialQuery,
      ypageSize: query.ypageSize,
      merchantId: values.merchantId ?? undefined,
      merchantName: values.merchantName?.trim() || undefined,
      merchantAccount: values.merchantAccount?.trim() || undefined,
      status: values.status,
      startTime: values.range?.[0]?.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      endTime: values.range?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    });
  };
  const handleReset = (): void => {
    form.resetFields();
    setSelectedKeys([]);
    setQuery({ ...initialQuery });
  };
  const columns: ColumnsType<MerchantWithdrawal> = [
    { title: "提现单号", dataIndex: "id" },
    { title: "商户ID", dataIndex: "merchantId" },
    { title: "商户名称", dataIndex: "merchantName" },
    { title: "商户账号", dataIndex: "merchantAccount" },
    { title: "身份证号", dataIndex: "idCardNo" },
    {
      title: "提现金额",
      dataIndex: "withdrawAmount",
      render: (value: number): string => `¥${Number(value).toFixed(2)}`,
    },
    { title: "开户银行", dataIndex: "bankName" },
    { title: "开户支行", dataIndex: "bankBranch" },
    { title: "银行账号", dataIndex: "bankAccount" },
    {
      title: "提现状态",
      render: (_, record): ReactElement => (
        <Tag color={statusColors[record.status]}>{record.statusName || statuses[record.status] || "未知"}</Tag>
      ),
    },
    { title: "申请日期", dataIndex: "applyTime", render: formatDateTime },
    { title: "审核日期", dataIndex: "auditTime", render: formatDateTime },
    { title: "驳回备注", dataIndex: "auditRemark" },
    { title: "到账时间", dataIndex: "arrivalTime", render: formatDateTime },
    {
      title: "操作",
      fixed: "right",
      render: (_, record): ReactElement | null =>
        record.status === 0 ? (
          <Space>
            <Button
              size="small"
              type="primary"
              disabled={loading || submitting}
              onClick={() => handleApprove([record.id])}
            >
              通过
            </Button>
            <Button
              size="small"
              danger
              disabled={loading || submitting}
              onClick={() => handleOpenReject([record.id])}
            >
              驳回
            </Button>
          </Space>
        ) : null,
    },
  ];
  return (
    <div>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        style={{ gap: 12 }}
      >
        <Form.Item label="商户ID" name="merchantId">
          <InputNumber min={1} precision={0} />
        </Form.Item>
        <Form.Item label="商户名称" name="merchantName">
          <Input allowClear />
        </Form.Item>
        <Form.Item label="商户账号" name="merchantAccount">
          <Input allowClear />
        </Form.Item>
        <Form.Item label="提现状态" name="status">
          <Select
            allowClear
            style={{ width: 140 }}
            options={statuses.map((label, value) => ({ label, value }))}
          />
        </Form.Item>
        <Form.Item label="申请日期" name="range">
          <DatePicker.RangePicker />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </Space>
      </Form>
      <Space style={{ margin: "16px 0" }}>
        <Button
          type="primary"
          loading={exporting}
          onClick={() => void handleExport()}
        >
          导出
        </Button>
        <Button
          disabled={!selectedIds.length || loading || submitting}
          onClick={() => handleApprove(selectedIds)}
        >
          批量通过
        </Button>
        <Button
          danger
          disabled={!selectedIds.length || loading || submitting}
          onClick={() => handleOpenReject(selectedIds)}
        >
          批量驳回
        </Button>
      </Space>
      <Table<MerchantWithdrawal>
        bordered
        size="small"
        rowKey="id"
        dataSource={rows}
        columns={columns}
        loading={loading}
        scroll={{ x: "max-content" }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
          getCheckboxProps: (record) => ({
            disabled: record.status !== 0 || loading || submitting,
          }),
        }}
        pagination={{
          current: query.ypageNum,
          pageSize: query.ypageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: (ypageNum, ypageSize): void => {
            setSelectedKeys([]);
            setQuery((current) => ({ ...current, ypageNum, ypageSize }));
          },
        }}
      />
      <Modal
        open={rejectIds.length > 0}
        title={`驳回 ${rejectIds.length} 笔提现申请`}
        onCancel={() => setRejectIds([])}
        onOk={() => rejectForm.submit()}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        maskClosable={!submitting}
      >
        <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
          <Form.Item
            name="auditRemark"
            label="驳回备注"
            rules={[
              { required: true, whitespace: true, message: "请输入驳回备注" },
            ]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
export default MerchantWithdrawalReview;
